import { Box, Button, DialogActions, DialogContent, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import Translate from "../../utils/Translate";
import { useSnackbar } from "notistack";
import { useApi } from "../../utils/Api";
import SelectActorPermitSkills from "./SelectActorPermitSkills";
import SelectActorPermitDetails from "./SelectActorPermitDetails";
import SelectActorPermitResponsibleActor from "./SelectActorPermitResponsibleActor";
import { validateResponsibleActor } from "./validation/ActorValidator";
import { validateActorPermitSkills, validateActorPermitValidFrom } from "./validation/ActorPermitValidator";
import ButtonWithSpinner from "../ButtonWithSpinner";
import CompanyCrudDialog from "../actors/crud/CompanyCrudDialog";
import { actorSearch, actorCompanyTypes } from "../../utils/actorSearch";
import AutocompleteSearchField from "../AutocompleteSearchField";
import ActorAutocompleteOption from "../ActorAutocompleteOption";
import { postActorPermitGroupForResponsibleView } from "../confirmPermits/permitApi";

export default function CreateActorPermitDialog({
  actors,
  onBack,
  onPermitGroupSaved,
  hasStep1,
  cloneFromActorPermit,
  responsibleActorId,
  apiGetActor = null,
  apiGetActorSkillForNewPermit = null,
  apiGetActorSkillGroupsForNewPermit = null,
  apiGetSuggestedValuesForNewPermit = null,
  showInPermitResponsibleView = false,
  dataId = null, // för permit responsible vyn
}) {

  const [responsibleActor, setResponsibleActor] = useState(null);
  const [selectedPermitSkills, setSelectedPermitSkills] = useState(cloneFromActorPermit?.skills ?? []);
  const [otherEquipment, setOtherEquipment] = useState(cloneFromActorPermit?.otherEquipment);
  const [task, setTask] = useState(cloneFromActorPermit?.task);
  const [area, setArea] = useState(cloneFromActorPermit?.area);
  const [permitStartDate, setPermitStartDate] = useState(new Date());
  const [permitEndDate, setPermitEndDate] = useState(null);

  const [suggestions, setSuggestions] = useState([]);

  const [company, setCompany] = useState(null);
  const [showCompanyCrud, setShowCompanyCrud] = useState(false);

  const [errors, setErrors] = useState({});
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const api = useApi();

  useEffect(() => {
    async function loadParentActor() {
      if(cloneFromActorPermit) {
        if(responsibleActorId) {
          const responsible = await apiGetActor(responsibleActorId);
          setResponsibleActor({
            actorId: responsibleActorId,
            actorName: responsible.actorName,
            email: responsible.actorEmail,
            orgNo: responsible.orgNo,
            phoneNumber: cloneFromActorPermit.mobilePhone,
          });
        } else {
          setResponsibleActor(cloneResponsibleActor(cloneFromActorPermit));
        }
      }

      let parentActorId = null;
      if(cloneFromActorPermit && cloneFromActorPermit.companyActorId) {
        parentActorId = cloneFromActorPermit.companyActorId;
      } else {
        const parentActorIds = 
          (actors || []).map(a => (a.childActorGroups || [])
            .map(ag => ag.parentActorId))
            .flatMap(id => id);

        parentActorId = parentActorIds.length > 0 
          ? parentActorIds.find(x => x !== 4) 
          : null; // skip ePortMaster
      }

      if(!parentActorId) {
        const parentActor = await api.fetch(
          `${import.meta.env.VITE_MAIN_URL}actors/parent-actor/for-data-id`,
          false,
          "GET",
        );

        parentActorId = parentActor?.actorId;
      }

      // if (!actors || actors.length === 0) {
      //   return;
      // }

      // const childActorGroups = actors[0]?.childActorGroups;
      // if (!childActorGroups || childActorGroups.length === 0) {
      //   return;
      // }

      // const firstParentActorId = childActorGroups[0].parentActorId;
      // if (!firstParentActorId) {
      //   return;
      // }

      // // Hoppa över om parentActor är "ePortMaster" (actorId = 4) eftersom det inte är ett riktigt företag
      // if (firstParentActorId === 4) {
      //   return;
      // }

      const result = await actorSearch(parentActorId, actorCompanyTypes, ["ActorId"], 1, true, null, showInPermitResponsibleView, dataId);
      const actor = Array.isArray(result) ? result[0] : result;
      if (!actor) {
        return;
      }

      const mapped = {
        actorId: actor.actorId,
        actorName: actor.actorName,
        orgNo: actor.orgNo,
        externalActorNo: actor.externalActorNo,
        email: actor.email,
        profilePictureUrl: actor.profilePictureUrl,
        phoneNumber: actor.phoneNumbers?.find((ph) => ph.type === 2)?.phoneNumber,
        companies:
          actor.parentActors
            ?.filter((x, index, all) => x.parentActor?.actorId > 0)
            .map((x) => ({ actorId: x.parentActor.actorId, actorName: x.parentActor.actorName })) ?? [],
      };

      setCompany(mapped);
    }

    loadParentActor();
  }, [actors, responsibleActorId]);

  function validate() {
    const validationError1 = validateActorPermitSkills(selectedPermitSkills);
    const validationError2 = validateResponsibleActor(responsibleActor);
    const validationError3 = validateActorPermitValidFrom(permitStartDate);
    const validationError = validationError1 || validationError2 || validationError3;

    if (validationError) {
      const errorMessage = enqueueSnackbar(validationError, {
        variant: "error",
        autoHideDuration: 6000,
        onClick: () => closeSnackbar(errorMessage),
      });
      return false;
    }

    return true;
  }

  function cloneResponsibleActor(cloneFromActorPermit) {
    if (cloneFromActorPermit == null) {
      return null;
    }

    return {
      actorId: cloneFromActorPermit.responsibleActorId,
      actorName: cloneFromActorPermit.responsibleActorName,
      email: cloneFromActorPermit.responsibleActorEmail,
      orgNo: cloneFromActorPermit.responsibleActorOrgNo,
      phoneNumber: cloneFromActorPermit.responsibleActorPhoneNumber,
    };
  }

  async function handleClickSave() {
    // Validation is already done when the values are selected and set.
    // But might as well do one last check before saving everything.
    if (!validate()) {
      return;
    }

    const dto = {
      actorPermitGroupId: 0,
      responsibleActorId: responsibleActor?.actorId,
      skillGroupId:
        (selectedPermitSkills && selectedPermitSkills.length && selectedPermitSkills[0].skillGroupId) || null,
      actorPermits: actors.map((a) => ({
        actorPermitId: 0,
        actorId: a.actorId,
        responsibleActorId: responsibleActor?.actorId,
        area: area,
        otherEquipment: otherEquipment,
        task: task,
        skills: selectedPermitSkills ?? [],
        validFrom: permitStartDate,
        validTo: permitEndDate,
        companyActorId: company.actorId,
      })),
    };

    const response = showInPermitResponsibleView
      ? await postActorPermitGroupForResponsibleView(dataId, dto)
      : await api.fetch(`${import.meta.env.VITE_MAIN_URL}permits/groups`, dto, "POST");

    if (response && response.isSuccessful) {
      await onPermitGroupSaved(response.actorPermitGroup.actorPermitGroupId);
    }
  }

  // We need to skip validation on initial selection of the default skills for the actors
  function handleSelectPermitSkills(skills, skipValidation = false) {
    const validationError = !skipValidation ? validateActorPermitSkills(skills) : null;
    setErrors({ ...errors, selectedPermitSkills: validationError });
    const sortedSkills = skills.sort((lhs, rhs) =>
      `${lhs.shortCode} ${lhs.description}`.localeCompare(`${rhs.shortCode} ${rhs.description}`)
    );
    setSelectedPermitSkills(sortedSkills);
  }

  function handleChangeResponsibleActor(actor) {
    if (actor) {
      const validationError = validateResponsibleActor(actor);
      setErrors({ ...errors, responsibleActor: validationError });
    }
    setResponsibleActor(actor);
  }

  function handleValidFromDateChange(date) {
    const validationError = validateActorPermitValidFrom(date);
    setErrors({ ...errors, permitStartDate: validationError });
    setPermitStartDate(date);
  }

  async function CompanySearch(searchText) {
    const foundActors = await actorSearch(searchText, actorCompanyTypes, ["ActorName", "OrgNo", "ExternalActorNo"], 50);
    if (foundActors) {
      return foundActors.map((actor) => ({
        actorId: actor.actorId,
        actorName: actor.actorName,
        orgNo: actor.orgNo,
        externalActorNo: actor.externalActorNo,
        email: actor.email,
        profilePictureUrl: actor.profilePictureUrl,
        phoneNumber: actor.phoneNumbers?.find((ph) => ph.type === 2)?.phoneNumber,
        companies: actor.parentActors
          ?.filter(
            (x, index, allParentActors) =>
              x.parentActor.actorId !== actor.actorId &&
              x.parentActor.actorId > 0 &&
              allParentActors.findIndex((y) => y.parentActor.actorId === x.parentActor.actorId) === index
          )
          .map((x) => ({
            actorId: x.parentActor.actorId,
            actorName: x.parentActor.actorName,
          })),
      }));
    }
  }

  function handleCompanyChange(company) {
    if (!company) {
      // This happens if the user presses the "Enter" key,
      // rather than selecting the "Add" from the list
      setCompany(null);
      return;
    }

    const isExistingActor = company.actorId > 0;
    if (!isExistingActor) {
      setShowCompanyCrud(true);
    }

    setCompany(company);
  }

  function handleCancelAddCompany() {
    setCompany(null);
    setShowCompanyCrud(false);
  }

  return (
    <>
      {showCompanyCrud && (
        <CompanyCrudDialog
          open={showCompanyCrud}
          companyCrudProps={{
            name: company.actorName ?? "",
            onClose: () => setShowCompanyCrud(false),
            onActorSaved: (actor) => setCompany(actor),
            actorId: company?.actorId,
            //parentActorId: drivers[driverToCrudIndex]?.companies?.[0]?.actorId ?? newActorParentActorId,
            //actorRole: "permit-holder",
            onCancel: () => handleCancelAddCompany(),
          }}
        />
      )}

      <DialogContent className="stdDialogContent">
        <Stack spacing={3}>
          <Stack direction="row">
            <Box className="textbox smallHeader blue" sx={{ padding: "4px 8px" }}>{`${Translate.get(
              "Step"
            )} 2 ${Translate.get("Of")} 2`}</Box>
          </Stack>
          <h2>{Translate.get("FillOutInfo")}</h2>
          <SelectActorPermitSkills
            actors={actors}
            selectedPermitSkills={selectedPermitSkills}
            onSelectedPermitSkillsChanged={handleSelectPermitSkills}
            errorMessage={errors.selectedPermitSkills}
            isCloneFromActorPermit={cloneFromActorPermit}
            setSuggestions={setSuggestions}
            apiGetActorSkillForNewPermit={apiGetActorSkillForNewPermit}
            apiGetActorSkillGroupsForNewPermit={apiGetActorSkillGroupsForNewPermit}
            apiGetSuggestedValuesForNewPermit={apiGetSuggestedValuesForNewPermit}
          />
          <Box>
            <SelectActorPermitDetails
              otherEquipment={otherEquipment}
              onOtherEquipmentChange={(value) => setOtherEquipment(value ?? "")}
              task={task}
              onTaskChange={(value) => setTask(value ?? "")}
              area={area}
              onAreaChange={(value) => setArea(value ?? "")}
              permitStartDate={permitStartDate}
              onPermitStartDateChange={handleValidFromDateChange}
              permitEndDate={permitEndDate}
              onPermitEndDateChange={setPermitEndDate}
              startDateErrorMessage={errors.permitStartDate}
              suggestedValues={suggestions}
            />
          </Box>
          <Box>
            <SelectActorPermitResponsibleActor
              responsibleActor={responsibleActor}
              onResponsibleActorChange={handleChangeResponsibleActor}
              errorMessage={errors.responsibleActor}
              showInPermitResponsibleView={showInPermitResponsibleView}
            />
          </Box>

          <div className="smallHeader">{Translate.get("Company")}</div>
          <AutocompleteSearchField
            label={Translate.get("Company") + " *"}
            value={company}
            onValueChange={handleCompanyChange}
            getOptionLabel={(option) => option?.actorName ?? ""}
            renderOption={(option) => (
              <ActorAutocompleteOption
                actorName={option?.actorName}
                orgNo={option?.orgNo}
                email={option?.email}
                companies={option?.companies?.map((x) => x.actorName)}
                parentActors={option?.parentActors}
                externalActorNo={option?.externalActorNo}
              />
            )}
            disabled={showInPermitResponsibleView}
            keyPropName="actorId"
            requireSelection={true}
            search={CompanySearch}
            createNewValue={(text) => ({
              actorName: text,
            })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onBack}>{hasStep1 ? Translate.get("Back") : Translate.get("Cancel")}</Button>
        <ButtonWithSpinner
          variant="contained"
          onClick={handleClickSave}
          disabled={
            Object.values(errors).some((v) => !!v) ||
            !responsibleActor ||
            !selectedPermitSkills ||
            selectedPermitSkills.length <= 0 ||
            !permitStartDate ||
            !task ||
            !company
          }
        >
          {Translate.get("Save")}
        </ButtonWithSpinner>
      </DialogActions>
    </>
  );
}
